import { ConfirmDialog } from '@/components/ConfirmDialog'
import { config } from '@/config/config'
import { cn } from '@/lib/utils'
import { ScheduledTemplate } from '@/types/schedule'
import Template from '@repo/datalayer/store/models/Template'
import TemplateItem from '@repo/datalayer/store/models/TemplateItem'
import {
  Edit,
  FileText,
  Plus,
  ArrowLeft,
  Settings2,
  Trash2,
  GripVertical,
  Clock,
  CalendarClock,
} from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Button } from '../../components/ui/button'
import { useRouter } from '../../hooks/router'
import { useStore } from '../../hooks/store'
import { EditTemplateItemDialog } from './EditTemplateItemDialog'
import { ScheduleTemplateDialog } from './ScheduleTemplateDialog'

export const TemplatePage = observer(function TemplatePage() {
  const store = useStore()
  const [template, setTemplate] = useState<Template | null>(null)
  const [schedules, setSchedules] = useState<ScheduledTemplate[]>([])
  const router = useRouter()
  const { id } = router.router.params as { id: string }
  const containerRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()

  const fetchSchedules = useCallback(async () => {
    try {
      const response = await fetch(
        `${config.apiEndpoint}/api/scheduled_templates?templateId=${id}`,
        { credentials: 'include' }
      )
      if (response.ok) {
        const fetchedSchedules: ScheduledTemplate[] = await response.json()
        setSchedules(fetchedSchedules)
      } else {
        console.error('Failed to fetch schedules:', response.status)
        toast.error('Failed to load schedules')
      }
    } catch (error) {
      console.error('Failed to fetch schedules:', error)
      toast.error('Failed to load schedules')
    }
  }, [id])

  useEffect(() => {
    store.fetchTemplate(id).then((template) => {
      if (template) {
        setTemplate(template)
      } else {
        // something went wrong
      }
    })
    fetchSchedules()
  }, [id, fetchSchedules])

  if (!template) return null

  const itemCount = template.items?.length || 0

  return (
    <main className="max-w-[900px] mx-auto w-full relative" ref={containerRef}>
      <div className="px-4 py-6">
        {/* Back button */}
        <button
          onClick={() => router.navigateToHome()}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Back to Home</span>
        </button>

        {/* Hero Header */}
        <div className="bg-toast rounded-3xl p-8 mb-6 text-forest relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-forest/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-forest/5 rounded-full translate-y-1/2 -translate-x-1/2" />

          <div className="relative flex flex-col md:flex-row md:items-center gap-6">
            <div className="w-20 h-20 rounded-2xl bg-forest/10 flex items-center justify-center flex-shrink-0">
              <FileText className="w-10 h-10" />
            </div>
            <div className="flex-1">
              <h1 data-test-id="template-title" className="text-2xl md:text-3xl font-bold mb-2">
                {template?.name}
              </h1>
              <p className="text-forest/70 text-lg">
                {itemCount} {itemCount === 1 ? 'item' : 'items'} in template
              </p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="relative flex flex-wrap items-center gap-3 mt-6 pt-6 border-t border-forest/20">
            <ScheduleTemplateDialog
              templateId={template.id}
              templateName={template.name}
              onScheduleChange={setSchedules}
            >
              <Button
                data-test-id="template-schedule-button"
                className={cn(
                  'border-0',
                  schedules.length > 0
                    ? 'bg-success/90 hover:bg-success text-white'
                    : 'bg-forest/10 hover:bg-forest/20 text-forest'
                )}
              >
                {schedules.length > 0 ? (
                  <>
                    <CalendarClock className="w-4 h-4 mr-2" />
                    <span data-test-id="schedule-button-label">
                      {schedules.length === 1
                        ? schedules[0].enabled
                          ? 'Scheduled'
                          : 'Paused'
                        : `${schedules.length} Schedules`}
                    </span>
                  </>
                ) : (
                  <>
                    <Clock className="w-4 h-4 mr-2" />
                    <span data-test-id="schedule-button-label">Schedule</span>
                  </>
                )}
              </Button>
            </ScheduleTemplateDialog>
            <Button
              data-test-id="template-reorder-button"
              onClick={() => {
                router.navigateToTemplateReorder(template.id)
              }}
              className="bg-forest/10 hover:bg-forest/20 text-forest border-0"
            >
              <Settings2 className="w-4 h-4 mr-2" />
              {t('templates.reorder')}
            </Button>
            <ConfirmDialog
              destructive
              confirmLabel={t('delete')}
              title={t('areYouSure')}
              description={t('templates.deleteConfirmation')}
              onConfirm={async () => {
                template.delete().then(() => {
                  toast.success(t('templates.deleted'))
                  router.navigateToHome()
                })
              }}
            >
              <Button
                data-test-id="template-delete-button"
                variant="secondary"
                className="bg-forest/10 text-forest hover:bg-destructive hover:text-white border-0"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </ConfirmDialog>
          </div>
        </div>

        {/* Template Items */}
        <div className="bg-card rounded-2xl shadow-sm overflow-hidden">
          <div className="divide-y">
            {template?.sortedItems.map((item) => (
              <TemplateItemView key={item.id} template={template} item={item} />
            ))}
          </div>

          {/* Add new item */}
          <div className="border-t bg-muted/30">
            <form
              className="flex items-center gap-3 p-4"
              onSubmit={(e) => {
                e.preventDefault()

                const target = e.target as HTMLFormElement
                const data = new FormData(target)
                const name = data.get('name') as string

                if (name) {
                  template?.addItem(name)
                  target.reset()
                  setTimeout(() => {
                    containerRef.current?.scrollTo({
                      top: containerRef.current.scrollHeight,
                      behavior: 'instant',
                    })
                  }, 100)
                }
              }}
            >
              <div className="p-2 rounded-lg bg-toast/20">
                <Plus className="w-5 h-5 text-forest" />
              </div>
              <input
                type="text"
                name="name"
                data-test-id="template-add-item-input"
                placeholder={t('templates.addNewItem')}
                className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground"
                autoComplete="off"
                data-1p-ignore
              />
              <Button
                type="submit"
                size="sm"
                data-test-id="template-add-item-submit"
                className="bg-toast-deep text-forest hover:bg-toast-deep/90"
              >
                {t('templates.add')}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </main>
  )
})

const TemplateItemView = observer(function TemplateItemView({
  template,
  item,
}: {
  template: Template
  item: TemplateItem
}) {
  const { t } = useTranslation()
  return (
    <div
      data-test-id="template-item"
      className={cn(
        'group flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors'
      )}
    >
      {/* Drag handle visual */}
      <div className="text-muted-foreground/40 group-hover:text-muted-foreground transition-colors">
        <GripVertical className="w-5 h-5" />
      </div>

      {/* Item content */}
      <div className="flex-1 min-w-0">
        <p data-test-id="template-item-name" className="font-medium truncate">
          {item.name}
          {item.requiresMedia && (
            <span className="ml-2 inline-flex items-center rounded-full bg-warning/20 px-2 py-0.5 text-[10px] font-semibold text-foreground">
              {t('checklists.mediaRequired')}
            </span>
          )}
        </p>
        {item.description && (
          <p className="text-sm text-muted-foreground mt-0.5">
            {item.description}
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <EditTemplateItemDialog item={item}>
          <Button data-test-id="template-item-edit-button" variant="ghost" size="sm" className="h-8 w-8 p-0">
            <Edit className="w-4 h-4" />
          </Button>
        </EditTemplateItemDialog>
        <ConfirmDialog
          destructive
          confirmLabel={t('delete')}
          title={t('areYouSure')}
          description={t('templates.deleteItemConfirmation')}
          onConfirm={async () => {
            item.delete()
          }}
        >
          <Button
            data-test-id="template-item-delete-button"
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </ConfirmDialog>
      </div>
    </div>
  )
})
